import { useRouter, type NextRouter } from 'next/router';

import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { routes } from '@/domains/core/routing/routes/routes.default';
import { createURL, parseURL } from '@/domains/core/routing/URLParser';
import { StringABTests as ListingsABTests } from '@/productDiscovery/Listings/constants';

import { LISTING_LAYOUT_ID, TECHNICAL_FILTER_URL_PREFIX } from '../constants';
import type { PlainQueryParams } from '../interfaces/algolia';
import { TechnicalFiltersOptions } from '../interfaces/filterConfig';
import type { ListingPage } from '../model';
import { stringify } from '../services/querystring';
import { usePageName } from './usePageName';

let pageName: ListingPage | null | undefined;
let asPath: NextRouter['asPath'];
let routerPush: NextRouter['push'];
const LANDING_SEO_FILTER = 'ls_filter';

function refine(
  filtersQueryParams: PlainQueryParams,
  clean = false,
  withScrollToTop = true,
  technicalFiltersOptions?: TechnicalFiltersOptions,
): void {
  const { pathParams, queryParams } = parseURL(
    asPath,
    routes[pageName as ListingPage],
  );

  const querystring = {
    ...(clean ? {} : queryParams),
    ...filtersQueryParams,
  };

  if (technicalFiltersOptions) {
    const { active, facetName, id } = technicalFiltersOptions;
    if (facetName === 'categories.last' && active) {
      Object.keys(querystring).forEach((key) => {
        if (key.startsWith(TECHNICAL_FILTER_URL_PREFIX) || key === 'colors') {
          delete querystring[key];
        }
        querystring.catId = id;
      });
    }
  }

  const shouldAddLandingSEOFilter: boolean = Object.entries(
    querystring,
  ).reduce<boolean>(
    (result, [key, value]) =>
      key !== LANDING_SEO_FILTER && value !== undefined ? true : result,
    false,
  );

  if (shouldAddLandingSEOFilter) {
    querystring[LANDING_SEO_FILTER] = 'false';
  } else {
    delete querystring[LANDING_SEO_FILTER];
  }

  const url = `${createURL(
    routes[pageName as ListingPage],
    pathParams as Record<string, string>,
  )}${stringify(querystring as PlainQueryParams)}`;
  routerPush(url, undefined, { shallow: true });

  if (withScrollToTop)
    document.getElementById(LISTING_LAYOUT_ID)?.scrollIntoView();
}

export const useRefine = (): ((
  queryParams: PlainQueryParams,
  clean?: boolean,
  withScrollToTop?: boolean,
  technicalFiltersOptions?: TechnicalFiltersOptions,
) => void) => {
  pageName = usePageName();
  const [removeScrollRelatedFilters] = useBooleanFlags([
    ListingsABTests.REMOVE_FILTERS_RELATED_SCROLL,
  ]);
  const router = useRouter();
  ({ asPath, push: routerPush } = router);

  return (queryParams, clean, withScrollToTop, technicalFiltersOptions) =>
    refine(
      queryParams,
      clean,
      removeScrollRelatedFilters ? false : withScrollToTop,
      technicalFiltersOptions,
    );
};
